import React, { Component } from 'react'
import { API_GET_SMTP_CONFIG, API_SAVE_SMTP_CONFIG, API_TEST_SMTP, ISMTPConfig } from '../api'
import { t } from '../i18n'

interface Props {
    workspaceId: string
}

interface State {
    cfg: ISMTPConfig
    testTo: string
    saving: boolean
    saved: boolean
    testing: boolean
    testResult: 'ok' | 'fail' | null
    loading: boolean
}

// 常用邮件服务商预设
const PRESETS: { label: string; server: string; port: string }[] = [
    { label: 'QQ 邮箱',     server: 'smtp.qq.com',      port: '465' },
    { label: '163 邮箱',    server: 'smtp.163.com',     port: '465' },
    { label: 'Gmail',       server: 'smtp.gmail.com',   port: '587' },
    { label: 'Outlook',     server: 'smtp.office365.com', port: '587' },
]

class SMTPSettings extends Component<Props, State> {
    constructor(props: Props) {
        super(props)
        this.state = {
            cfg: { smtpServer: '', smtpPort: '465', smtpUser: '', smtpPass: '', emailFrom: '' },
            testTo: '',
            saving: false,
            saved: false,
            testing: false,
            testResult: null,
            loading: true,
        }
    }

    componentDidMount() {
        API_GET_SMTP_CONFIG(this.props.workspaceId)
            .then(r => r.ok ? r.json() : null)
            .then((data: ISMTPConfig | null) => {
                if (data) this.setState({ cfg: { ...data, smtpPass: '' }, loading: false })
                else this.setState({ loading: false })
            })
    }

    handleChange = (field: keyof ISMTPConfig, value: string) => {
        this.setState(s => ({ cfg: { ...s.cfg, [field]: value }, saved: false, testResult: null }))
    }

    applyPreset = (preset: typeof PRESETS[0]) => {
        this.setState(s => ({ cfg: { ...s.cfg, smtpServer: preset.server, smtpPort: preset.port } }))
    }

    handleSave = async () => {
        this.setState({ saving: true, saved: false })
        const r = await API_SAVE_SMTP_CONFIG(this.props.workspaceId, this.state.cfg)
        this.setState({ saving: false, saved: r.ok })
        if (r.ok) setTimeout(() => this.setState({ saved: false }), 3000)
    }

    handleTest = async () => {
        if (!this.state.testTo) return
        this.setState({ testing: true, testResult: null })
        const r = await API_TEST_SMTP(this.props.workspaceId, this.state.testTo)
        this.setState({ testing: false, testResult: r.ok ? 'ok' : 'fail' })
    }

    render() {
        const { cfg, testTo, saving, saved, testing, testResult, loading } = this.state
        if (loading) return <div className="p-2 text-xs text-gray-400">Loading...</div>

        const inputCls = "rounded border p-1 w-full text-sm"
        const labelCls = "text-xs text-gray-500 mb-1"

        return (
            <div className="mt-4">
                <div className="font-semibold text-sm mb-1">{t('smtp_title')}</div>
                <div className="text-xs text-gray-500 mb-3">{t('smtp_desc')}</div>

                {/* 快速预设 */}
                <div className="mb-3">
                    <div className={labelCls}>{t('smtp_preset')}</div>
                    <div className="flex flex-wrap gap-1">
                        {PRESETS.map(p => (
                            <button
                                key={p.label}
                                onClick={() => this.applyPreset(p)}
                                className="text-xs border rounded px-2 py-1 hover:bg-gray-100"
                            >{p.label}</button>
                        ))}
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-3 max-w-lg">
                    {/* SMTP Server */}
                    <div className="col-span-2 flex gap-2">
                        <div className="flex-1">
                            <div className={labelCls}>{t('smtp_server')}</div>
                            <input className={inputCls} value={cfg.smtpServer}
                                placeholder="smtp.qq.com"
                                onChange={e => this.handleChange('smtpServer', e.target.value)} />
                        </div>
                        <div style={{ width: 80 }}>
                            <div className={labelCls}>{t('smtp_port')}</div>
                            <input className={inputCls} value={cfg.smtpPort}
                                placeholder="465"
                                onChange={e => this.handleChange('smtpPort', e.target.value)} />
                        </div>
                    </div>

                    {/* User */}
                    <div className="col-span-2">
                        <div className={labelCls}>{t('smtp_user')}</div>
                        <input className={inputCls} value={cfg.smtpUser}
                            placeholder="your@email.com"
                            onChange={e => this.handleChange('smtpUser', e.target.value)} />
                    </div>

                    {/* Password / Auth Code */}
                    <div className="col-span-2">
                        <div className={labelCls}>{t('smtp_pass')}</div>
                        <input className={inputCls} type="password" value={cfg.smtpPass}
                            placeholder={t('smtp_pass_hint')}
                            onChange={e => this.handleChange('smtpPass', e.target.value)} />
                    </div>

                    {/* From */}
                    <div className="col-span-2">
                        <div className={labelCls}>{t('smtp_from')}</div>
                        <input className={inputCls} value={cfg.emailFrom}
                            placeholder="your@email.com"
                            onChange={e => this.handleChange('emailFrom', e.target.value)} />
                    </div>
                </div>

                {/* 保存按钮 */}
                <div className="flex items-center gap-2 mt-3">
                    <button
                        onClick={this.handleSave}
                        disabled={saving}
                        className="text-xs bg-blue-500 text-white rounded px-3 py-1 hover:bg-blue-600 disabled:opacity-50"
                    >
                        {saving ? '...' : t('smtp_save')}
                    </button>
                    {saved && <span className="text-xs text-green-600">{t('smtp_saved')}</span>}
                </div>

                {/* 发送测试邮件 */}
                <div className="mt-4 border-t pt-3">
                    <div className={labelCls}>{t('smtp_test')}</div>
                    <div className="flex items-center gap-2 max-w-lg">
                        <input
                            className="rounded border p-1 text-sm flex-1"
                            value={testTo}
                            placeholder={t('smtp_test_to')}
                            onChange={e => this.setState({ testTo: e.target.value, testResult: null })}
                        />
                        <button
                            onClick={this.handleTest}
                            disabled={testing || !testTo}
                            className="text-xs border rounded px-3 py-1 hover:bg-gray-100 disabled:opacity-50 whitespace-nowrap"
                        >
                            {testing ? '...' : t('smtp_test')}
                        </button>
                    </div>
                    {testResult === 'ok' && <p className="text-xs text-green-600 mt-1">{t('smtp_test_ok')}</p>}
                    {testResult === 'fail' && <p className="text-xs text-red-500 mt-1">{t('smtp_test_fail')}</p>}
                </div>
            </div>
        )
    }
}

export default SMTPSettings
